import axios, { AxiosInstance } from 'axios';
import { attachAuthRefreshInterceptor } from './authService';

// Configuración de la API
const API_BASE_URL = (import.meta as any).env.VITE_API_URL || 'https://crm.cainguas.cloud/api';

// Tipos
export interface Conversation {
  id: string;
  phoneNumber: string;
  customerName?: string;
  profilePic?: string | null;
  status: 'ACTIVE' | 'QUEUED' | 'CLOSED';
  assignedAgent?: string;
  agentName?: string;
  departmentId?: number | null;
  departmentName?: string;
  lastMessage?: {
    content: string;
    timestamp: string;
    direction: 'INBOUND' | 'OUTBOUND';
  };
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  queuePosition?: number;
  waitTime?: number;
  createdAt: string;
  updatedAt: string;
  tags?: string[];
  metadata?: any;
  // Canal por el que escribe el cliente: WhatsApp o la app de autogestión
  channel?: 'WHATSAPP' | 'APP';
  // Flag opcional para identificar conversaciones completamente nuevas
  isNew?: boolean;
  // Nuevos campos de control de conversación
  control?: 'BOT' | 'HUMAN' | 'UNASSIGNED';
  controlledBy?: string;
  controlledByUsername?: string;
  controlType?: 'AGENT' | 'TAKEOVER';
  controlledSince?: string;
  takeoverNotes?: string;
  hasActiveTakeover?: boolean;
}

export interface RealtimeMetrics {
  totalConversations: number;
  activeConversations: number;
  queuedConversations: number;
  closedToday: number;
  averageResponseTime: number;
  averageWaitTime: number;
  customerSatisfaction: number;
  agentPerformance: Array<{
    agentId: string;
    name: string;
    status: 'ONLINE' | 'OFFLINE' | 'BUSY' | 'AWAY';
    activeChats: number;
    averageResponseTime: number;
    satisfaction: number;
    totalHandled: number;
  }>;
  systemHealth: {
    cpu: number;
    memory: number;
    uptime: number;
    errors: number;
  };
  timestamp: string;
}

export interface SystemConfig {
  id: string;
  region: string;
  timezone: string;
  businessHours: {
    start: string;
    end: string;
    days: string[];
  };
  maxConcurrentChats: number;
  autoAssignment: boolean;
  queueSettings: {
    maxWaitTime: number;
    priorityWeights: {
      LOW: number;
      MEDIUM: number;
      HIGH: number;
      URGENT: number;
    };
  };
  whatsappConfig: {
    templates: Array<{
      id: string;
      name: string;
      content: string;
      category: string;
    }>;
    autoResponses: {
      greeting: string;
      queueMessage: string;
      closingMessage: string;
    };
  };
  notifications: {
    email: boolean;
    sms: boolean;
    webhook: string;
  };
}

export interface AuditLog {
  id: string;
  userId: string;
  userName: string;
  action: string;
  resource: string;
  resourceId?: string;
  details: any;
  ipAddress: string;
  userAgent: string;
  timestamp: string;
}

// Observabilidad P4 — shape del endpoint /api/dashboard/health-deep
export interface HealthCheck {
  ok: boolean;
  [key: string]: any;
}
export interface HealthAlert {
  severity: 'critical' | 'warning';
  area: string;
  message: string;
}
export interface SystemHealth {
  timestamp: string;
  status: 'ok' | 'warning' | 'critical';
  uptime_seconds: number;
  checks: {
    db: HealthCheck;
    queue: HealthCheck;
    circuit_breaker: HealthCheck;
    memory: HealthCheck;
    websocket: HealthCheck;
  };
  alerts: HealthAlert[];
  thresholds?: Record<string, number>;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message: string;
  error?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Tipos para el Bot de Deudas
export interface DebtBotConfig {
  id: string;
  enabled: boolean;
  activationKeywords: string[];
  confidenceThreshold: number;
  maxRetries: number;
  responseTimeout: number;
  humanTransferThreshold: number;
  externalDatabase: {
    enabled: boolean;
    connectionString: string;
    username: string;
    password?: string;
    tableName: string;
    queryTimeout: number;
    maxConnections: number;
  };
  validationRules: {
    dniMinLength: number;
    dniMaxLength: number;
    dniPattern: string;
    maxValidationAttempts: number;
    validationTimeout: number;
  };
  conversationFlow: {
    greetingMessage: string;
    dniRequestMessage: string;
    dataConfirmationMessage: string;
    debtSummaryMessage: string;
    paymentOptionsMessage: string;
    humanTransferMessage: string;
    noDebtFoundMessage: string;
    errorMessage: string;
    validationRetryMessage: string;
  };
  metrics: {
    logInteractions: boolean;
    logLevel: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';
    retentionDays: number;
    realtimeUpdates: boolean;
  };
  security: {
    encryptSensitiveData: boolean;
    maskDniInLogs: boolean;
    rateLimiting: {
      enabled: boolean;
      maxRequests: number;
      windowMs: number;
    };
  };
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

export interface ExternalDatabaseConfig {
  id: string;
  name: string;
  type: 'MYSQL' | 'POSTGRESQL' | 'SQLSERVER' | 'ORACLE';
  host: string;
  port: number;
  database: string;
  username: string;
  password?: string;
  ssl: boolean;
  connectionTimeout: number;
  queryTimeout: number;
  isActive: boolean;
  lastConnectionTest?: string;
  connectionStatus: 'CONNECTED' | 'DISCONNECTED' | 'ERROR' | 'TESTING';
}

export interface BotTemplate {
  id: string;
  name: string;
  type: 'GREETING' | 'DNI_REQUEST' | 'DATA_CONFIRMATION' | 'DEBT_SUMMARY' | 'PAYMENT_OPTIONS' | 'HUMAN_TRANSFER' | 'NO_DEBT_FOUND' | 'ERROR' | 'VALIDATION_RETRY';
  content: string;
  variables: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Menú de bienvenida para el chatbot
export interface MenuOption {
  key: string;
  title: string;
  description?: string;
}

export interface MenuConfiguration {
  id: string;
  configId: string;
  name: string;
  displayText: string;
  menuOptions: MenuOption[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

class DashboardService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Interceptor de request para agregar token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('dashboard_token');
        if (token) {
          (config.headers as any).Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Auto-refresh + retry en 401. Si el refresh falla, recién ahí redirigimos al login.
    attachAuthRefreshInterceptor(this.api, () => {
      window.location.href = '/login';
    });
  }

  // Métricas en tiempo real
  async getRealtimeMetrics(): Promise<RealtimeMetrics> {
    try {
      const response = await this.api.get<ApiResponse<RealtimeMetrics>>('/dashboard/metrics/realtime');

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Error al obtener métricas');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al obtener métricas en tiempo real');
    }
  }

  // Observabilidad P4 — health-deep devuelve JSON directo (no envuelto en ApiResponse).
  // El endpoint puede devolver HTTP 503 si status='critical': axios lanza excepción por
  // status >= 400 por default, pero el body todavía es JSON válido con la info que queremos.
  async getSystemHealth(): Promise<SystemHealth> {
    try {
      const response = await this.api.get<SystemHealth>('/dashboard/health-deep');
      return response.data;
    } catch (error: any) {
      // Si el backend respondió 503 con body válido, todavía queremos exponer ese estado.
      if (error.response?.data && typeof error.response.data === 'object' && error.response.data.status) {
        return error.response.data as SystemHealth;
      }
      throw new Error(error.response?.data?.message || error.message || 'Error consultando health-deep');
    }
  }

  // Métodos para el Bot de Deudas
  async getDebtBotConfig(): Promise<DebtBotConfig> {
    const response = await this.api.get<ApiResponse<any>>('/debt-bot/config');
    const raw = response.data.data || {};

    // Normalizar estructura a DebtBotConfig con defaults seguros
    const extRaw = raw.externalDbConfig || raw.externalDatabase || {};

    // Construir externalDatabase respetando exactOptionalPropertyTypes (omitir password si no existe)
    const externalDatabase: DebtBotConfig['externalDatabase'] = {
      enabled: !!extRaw.enabled,
      connectionString: extRaw.connectionString || '',
      username: extRaw.username || '',
      tableName: extRaw.tableName || 'member_debts',
      queryTimeout: typeof extRaw.queryTimeout === 'number' ? extRaw.queryTimeout : 30000,
      maxConnections: typeof extRaw.maxConnections === 'number' ? extRaw.maxConnections : 5,
    };
    if (typeof extRaw.password === 'string' && extRaw.password.length > 0) {
      externalDatabase.password = extRaw.password;
    }

    const normalized: DebtBotConfig = {
      id: raw.id || 'default-config',
      enabled: !!raw.isActive,
      activationKeywords: raw.activationKeywords || ['deuda', 'saldo', 'pago'],
      confidenceThreshold: typeof raw.confidenceThreshold === 'number' ? raw.confidenceThreshold : 0.7,
      maxRetries: typeof raw.maxRetries === 'number' ? raw.maxRetries : 3,
      responseTimeout: typeof raw.responseTimeout === 'number' ? raw.responseTimeout : 15000,
      humanTransferThreshold: typeof raw.humanTransferThreshold === 'number' ? raw.humanTransferThreshold : 0.85,
      externalDatabase,
      validationRules: {
        dniMinLength: raw.validationRules?.dniMinLength ?? 7,
        dniMaxLength: raw.validationRules?.dniMaxLength ?? 8,
        dniPattern: raw.validationRules?.dniPattern || '^\\d{7,8}$',
        maxValidationAttempts: raw.validationRules?.maxValidationAttempts ?? 3,
        validationTimeout: raw.validationRules?.validationTimeout ?? 120000,
      },
      conversationFlow: {
        greetingMessage: raw.conversationFlow?.greetingMessage || 'Hola! Soy el asistente de deudas. ¿Te ayudo a consultar tu estado?',
        dniRequestMessage: raw.conversationFlow?.dniRequestMessage || 'Por favor, enviá tu DNI para continuar.',
        dataConfirmationMessage: raw.conversationFlow?.dataConfirmationMessage || '¿Confirmás que estos datos son correctos?',
        debtSummaryMessage: raw.conversationFlow?.debtSummaryMessage || 'Este es tu resumen de deudas:',
        paymentOptionsMessage: raw.conversationFlow?.paymentOptionsMessage || 'Opciones de pago disponibles:',
        humanTransferMessage: raw.conversationFlow?.humanTransferMessage || 'Te transfiero con un agente humano para continuar.',
        noDebtFoundMessage: raw.conversationFlow?.noDebtFoundMessage || 'No encontramos deudas pendientes a tu nombre.',
        errorMessage: raw.conversationFlow?.errorMessage || 'Ocurrió un error procesando tu solicitud.',
        validationRetryMessage: raw.conversationFlow?.validationRetryMessage || 'El formato no es válido, intentá nuevamente.',
      },
      metrics: {
        logInteractions: raw.metrics?.logInteractions ?? true,
        logLevel: raw.metrics?.logLevel || 'INFO',
        retentionDays: raw.metrics?.retentionDays ?? 30,
        realtimeUpdates: raw.metrics?.realtimeUpdates ?? true,
      },
      security: {
        encryptSensitiveData: raw.security?.encryptSensitiveData ?? true,
        maskDniInLogs: raw.security?.maskDniInLogs ?? true,
        rateLimiting: {
          enabled: raw.security?.rateLimiting?.enabled ?? true,
          maxRequests: raw.security?.rateLimiting?.maxRequests ?? 50,
          windowMs: raw.security?.rateLimiting?.windowMs ?? 15 * 60 * 1000,
        },
      },
      createdAt: raw.createdAt || new Date().toISOString(),
      updatedAt: raw.updatedAt || raw.createdAt || new Date().toISOString(),
      updatedBy: raw.createdBy || 'system',
    };

    return normalized;
  }

  async updateDebtBotConfig(config: Partial<DebtBotConfig>): Promise<DebtBotConfig> {
    try {
      const response = await this.api.put<ApiResponse<DebtBotConfig>>('/debt-bot/config', config);
      return response.data.data!;
    } catch (error: any) {
      const status = error?.response?.status;
      const message = String(error?.response?.data?.message || '').toLowerCase();

      // Fallback: si no existe configuración activa (404), crear una y activarla, luego reintentar
      if (status === 404 && (message.includes('no existe configuración activa') || message.includes('configuracion activa'))) {
        try {
          // Crear configuración por defecto
          const createResp = await this.api.post<ApiResponse<any>>('/chatbot-config/configurations', {
            name: 'Debt Bot Config',
            description: 'Configuración inicial creada desde dashboard',
            isDefault: true,
          });
          const newConfigId = createResp?.data?.data?.id;

          if (newConfigId) {
            // Activar la configuración recién creada
            await this.api.put<ApiResponse<any>>(`/chatbot-config/configurations/${newConfigId}`, {
              isActive: true,
              isDefault: true,
            });
          }

          // Reintentar actualización de configuración del debt-bot
          const retry = await this.api.put<ApiResponse<DebtBotConfig>>('/debt-bot/config', config);
          return retry.data.data!;
        } catch (fallbackError: any) {
          throw new Error(fallbackError?.response?.data?.message || 'No se pudo crear/activar una configuración por defecto');
        }
      }

      // Propagar error con mensaje claro
      throw new Error(error?.response?.data?.message || 'Error al actualizar configuración del bot de deudas');
    }
  }

  async updateDebtBotExternalDatabase(details: {
    dbType: 'MYSQL' | 'POSTGRESQL';
    host: string;
    port: number;
    database: string;
    username: string;
    password: string;
    ssl?: boolean;
    userQuery?: string;
    debtQuery?: string;
  }): Promise<DebtBotConfig> {
    const payload: any = {
      externalDatabase: {
        enabled: true,
        dbType: details.dbType,
        host: details.host,
        port: details.port,
        database: details.database,
        username: details.username,
        password: details.password,
        ssl: details.ssl ?? true,
        userQuery: details.userQuery,
        debtQuery: details.debtQuery
      }
    };
    const response = await this.api.put<ApiResponse<DebtBotConfig>>('/debt-bot/config', payload);
    return response.data.data!;
  }

  async testExternalDatabaseConnection(config: ExternalDatabaseConfig): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post<ApiResponse<{ success: boolean; message: string }>>('/debt-bot/test-connection', config);
    return response.data.data!;
  }

  async getExternalDatabaseConfigs(): Promise<ExternalDatabaseConfig[]> {
    try {
      const response = await this.api.get<ApiResponse<ExternalDatabaseConfig[]>>('/debt-bot/external-databases');
      // Si la API responde sin datos, devolvemos lista vacía
      return response.data.data ?? [];
    } catch (error: any) {
      // Fallback defensivo: ante 500/401/network error, no romper la UI
      console.warn('getExternalDatabaseConfigs failed, returning empty list:', error?.response?.status || error);
      return [];
    }
  }

  async createExternalDatabaseConfig(config: Omit<ExternalDatabaseConfig, 'id' | 'createdAt' | 'updatedAt'>): Promise<ExternalDatabaseConfig> {
    const response = await this.api.post<ApiResponse<ExternalDatabaseConfig>>('/debt-bot/external-databases', config);
    return response.data.data!;
  }

  async updateExternalDatabaseConfig(id: string, config: Partial<ExternalDatabaseConfig>): Promise<ExternalDatabaseConfig> {
    const response = await this.api.put<ApiResponse<ExternalDatabaseConfig>>(`/debt-bot/external-databases/${id}`, config);
    return response.data.data!;
  }

  async deleteExternalDatabaseConfig(id: string): Promise<void> {
    await this.api.delete<ApiResponse<void>>(`/debt-bot/external-databases/${id}`);
  }

  async getBotTemplates(): Promise<BotTemplate[]> {
    const response = await this.api.get<ApiResponse<any[]>>('/debt-bot/templates');
    const items = response.data.data || [];
    // Normalizar respuesta combinada (default + BD) a BotTemplate
    return items.map((item: any) => ({
      id: String(item.id || item.type || Math.random().toString(36).slice(2)),
      name: String(item.name || item.type || 'Template'),
      type: String(item.type || 'GREETING') as BotTemplate['type'],
      content: String(item.content || item.text || ''),
      variables: Array.isArray(item.variables) ? item.variables : [],
      isActive: typeof item.isActive === 'boolean' ? item.isActive : true,
      createdAt: String(item.createdAt || new Date().toISOString()),
      updatedAt: String(item.updatedAt || new Date().toISOString()),
    }));
  }

  async updateBotTemplate(id: string, template: Partial<BotTemplate>): Promise<BotTemplate> {
    const response = await this.api.put<ApiResponse<BotTemplate>>(`/debt-bot/templates/${id}`, template);
    return response.data.data!;
  }

  // CRUD adicional de plantillas vía endpoints generales de chatbot-config
  async createChatbotTemplate(payload: {
    configId: string;
    templateType: BotTemplate['type'];
    name: string;
    content: string;
    variables?: string[];
    priority?: number;
  }): Promise<BotTemplate> {
    const response = await this.api.post<ApiResponse<any>>('/chatbot-config/templates', {
      ...payload,
      variables: payload.variables?.length ? JSON.stringify(payload.variables) : null,
    });
    const data = response.data.data;
    return {
      id: String(data.id),
      name: data.name,
      type: data.templateType as BotTemplate['type'],
      content: data.content,
      variables: typeof data.variables === 'string' ? JSON.parse(data.variables) : (data.variables || []),
      isActive: !!data.isActive,
      createdAt: String(data.createdAt),
      updatedAt: String(data.updatedAt),
    };
  }

  async deleteChatbotTemplate(id: string): Promise<void> {
    await this.api.delete<ApiResponse<void>>(`/chatbot-config/templates/${id}`);
  }

  // Menús de bienvenida (chatbot-config)
  async getWelcomeMenus(configId: string): Promise<MenuConfiguration[]> {
    const response = await this.api.get<ApiResponse<any>>(`/chatbot-config/menus/${configId}`);
    const items = response.data.data || [];
    return items.map((m: any) => ({
      id: String(m.id),
      configId: String(m.configId),
      name: String(m.name),
      displayText: String(m.displayText),
      menuOptions: Array.isArray(m.menuOptions) ? m.menuOptions : [],
      isActive: !!m.isActive,
      createdAt: String(m.createdAt),
      updatedAt: String(m.updatedAt),
    }));
  }

  async createWelcomeMenu(payload: {
    configId: string;
    name: string;
    displayText: string;
    menuOptions: MenuOption[];
  }): Promise<MenuConfiguration> {
    const response = await this.api.post<ApiResponse<any>>('/chatbot-config/menus', payload);
    const m = response.data.data;
    return {
      id: String(m.id),
      configId: String(m.configId),
      name: String(m.name),
      displayText: String(m.displayText),
      menuOptions: Array.isArray(m.menuOptions) ? m.menuOptions : [],
      isActive: !!m.isActive,
      createdAt: String(m.createdAt),
      updatedAt: String(m.updatedAt),
    };
  }

  async getDebtBotMetrics(timeframe: '24h' | '7d' | '30d' | '90d' = '24h'): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(`/debt-bot/metrics/${timeframe}`);
    return response.data.data!;
  }

  async getDebtBotInteractionLogs(filters?: {
    startDate?: string;
    endDate?: string;
    status?: string;
    limit?: number;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await this.api.get<ApiResponse<any>>(`/debt-bot/logs?${params.toString()}`);
    return response.data.data!;
  }

  // Conversaciones
  async getConversations(filters?: {
    status?: string;
    agent?: string;
    priority?: string;
    tag?: string;
    page?: number;
    limit?: number;
    departmentId?: number;
    onlyMine?: boolean;
  }): Promise<{ conversations: Conversation[]; pagination: any }> {
    try {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined) {
            params.append(key, value.toString());
          }
        });
      }

      // La API devuelve { success, data: { conversations, pagination } }
      const response = await this.api.get<ApiResponse<{ conversations: Conversation[]; pagination: any }>>(`/dashboard/conversations?${params}`);

      if (response.data.success && response.data.data) {
        const { conversations, pagination } = response.data.data as { conversations: Conversation[]; pagination: any };
        return {
          conversations: conversations || [],
          pagination: pagination || {},
        };
      } else {
        throw new Error(response.data.message || 'Error al obtener conversaciones');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al obtener conversaciones');
    }
  }

  async getDepartments(): Promise<Array<{ id: number; name: string; slug: string; isActive: boolean }>> {
    try {
      const response = await this.api.get<ApiResponse<any>>('/dashboard/admin/departments');
      if (response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      return [];
    } catch (error: any) {
      return [];
    }
  }

  async createDepartment(payload: { name: string; slug: string; isActive?: boolean }) {
    const res = await this.api.post<ApiResponse>('/dashboard/admin/departments', payload);
    return res.data;
  }

  async updateDepartment(id: number, payload: Partial<{ name: string; slug: string; isActive: boolean }>) {
    const res = await this.api.put<ApiResponse>(`/dashboard/admin/departments/${id}`, payload);
    return res.data;
  }

  async deactivateDepartment(id: number) {
    const res = await this.api.delete<ApiResponse>(`/dashboard/admin/departments/${id}`);
    return res.data;
  }

  async assignUserDepartments(userId: string | number, departmentIds: number[], replace: boolean = true) {
    const res = await this.api.post<ApiResponse>(`/dashboard/admin/users/${userId}/departments`, { departmentIds, replace });
    return res.data;
  }

  async getUserDepartments(userId: string | number): Promise<number[]> {
    const res = await this.api.get<ApiResponse<any>>(`/dashboard/admin/users/${userId}/departments`);
    const items = Array.isArray(res.data?.data) ? res.data.data : [];
    return items.map((x: any) => parseInt(x.departmentId)).filter((n: any) => Number.isFinite(n));
  }

  // Obtener conversación específica
  async getConversation(conversationId: string, options?: { since?: string; before?: string; limit?: number }): Promise<Conversation> {
    try {
      const params = new URLSearchParams();
      if (options?.since) params.set('since', options.since);
      if (options?.before) params.set('before', options.before);
      if (typeof options?.limit === 'number') params.set('limit', String(options.limit));
      const qs = params.toString();
      const url = `/dashboard/conversations/${conversationId}${qs ? `?${qs}` : ''}`;
      const response = await this.api.get<ApiResponse<Conversation>>(url);
      if (response.data.success && response.data.data) {
        return response.data.data as any;
      } else {
        throw new Error(response.data.message || 'Error al obtener conversación');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al obtener conversación');
    }
  }

  // Tomar control de conversación
  async takeoverConversation(conversationId: string, reason?: string): Promise<void> {
    try {
      const response = await this.api.post<ApiResponse>(`/dashboard/conversations/${conversationId}/takeover`, {
        reason,
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al tomar control de la conversación');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al tomar control de la conversación');
    }
  }

  // Liberar conversación
  async releaseConversation(conversationId: string, reason?: string): Promise<void> {
    try {
      const response = await this.api.post<ApiResponse>(`/dashboard/conversations/${conversationId}/release`, {
        reason,
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al liberar conversación');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al liberar conversación');
    }
  }

  // Actualizar etiquetas del chat (máx 1)
  async updateConversationTags(conversationId: string, tags: string[]): Promise<string[]> {
    try {
      const response = await this.api.patch<ApiResponse<{ chatId: number; tags: string[] }>>(
        `/dashboard/conversations/${conversationId}/tags`,
        { tags }
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || 'Error al actualizar etiquetas');
      }
      return response.data.data.tags;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al actualizar etiquetas');
    }
  }

  // Finalizar turno: libera TODAS las conversaciones activas del operador actual
  async releaseAllMyConversations(): Promise<{ count: number; message: string }> {
    try {
      const response = await this.api.post<ApiResponse<{ count: number; message: string }>>(
        '/dashboard/conversations/release-all-mine'
      );
      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al finalizar turno');
      }
      return response.data.data!;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al finalizar turno');
    }
  }

  // Admin: obtener agentes con takeovers activos
  async getAgentsWithActiveTakeovers(): Promise<Array<{ userId: string; username: string; fullName: string; role: string; count: number }>> {
    try {
      const response = await this.api.get<ApiResponse<Array<{ userId: string; username: string; fullName: string; role: string; count: number }>>>(
        '/dashboard/admin/agents/active-takeovers'
      );
      return response.data.data ?? [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al obtener agentes');
    }
  }

  // Admin: liberar TODAS las conversaciones de un agente específico
  async releaseAgentConversations(userId: string): Promise<{ count: number; message: string }> {
    try {
      const response = await this.api.post<ApiResponse<{ count: number; message: string }>>(
        `/dashboard/admin/agents/${userId}/release-all`
      );
      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al liberar turno del agente');
      }
      return response.data.data!;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al liberar turno del agente');
    }
  }

  // Cerrar conversación
  async closeConversation(conversationId: string, reason?: string): Promise<void> {
    try {
      const response = await this.api.post<ApiResponse>(`/dashboard/conversations/${conversationId}/close`, {
        reason,
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al cerrar la conversación');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al cerrar la conversación');
    }
  }

  // Configuración del sistema
  async getSystemConfig(): Promise<SystemConfig> {
    try {
      const response = await this.api.get<ApiResponse<SystemConfig>>('/dashboard/config');

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Error al obtener configuración');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al obtener configuración del sistema');
    }
  }

  // Actualizar configuración regional
  async updateRegionalConfig(config: Partial<SystemConfig>): Promise<void> {
    try {
      const response = await this.api.put<ApiResponse>('/dashboard/config/regional', config);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al actualizar configuración');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al actualizar configuración regional');
    }
  }

  // Actualizar plantillas de WhatsApp
  async updateWhatsAppTemplates(templates: any[]): Promise<void> {
    try {
      const response = await this.api.put<ApiResponse>('/dashboard/config/whatsapp-templates', {
        templates,
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al actualizar plantillas');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al actualizar plantillas de WhatsApp');
    }
  }

  // Logs de auditoría
  async getAuditLogs(filters?: {
    userId?: string;
    action?: string;
    resource?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<{ logs: AuditLog[]; pagination: any }> {
    try {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined) {
            params.append(key, value.toString());
          }
        });
      }

      const response = await this.api.get<ApiResponse<AuditLog[]>>(`/dashboard/audit-logs?${params}`);

      if (response.data.success && response.data.data) {
        return {
          logs: response.data.data,
          pagination: response.data.pagination || {},
        };
      } else {
        throw new Error(response.data.message || 'Error al obtener logs');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al obtener logs de auditoría');
    }
  }

  // Reportes y analytics
  async getPerformanceReport(period: 'day' | 'week' | 'month' | 'year', startDate?: string, endDate?: string): Promise<any> {
    try {
      const params = new URLSearchParams({ period });
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await this.api.get<ApiResponse>(`/dashboard/reports/performance?${params}`);

      if (response.data.success) {
        return response.data.data || {};
      } else {
        throw new Error(response.data.message || 'Error al obtener reporte');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al obtener reporte de rendimiento');
    }
  }

  // Exportar datos
  async exportData(type: 'conversations' | 'metrics' | 'audit-logs', format: 'csv' | 'xlsx' | 'json', filters?: any): Promise<Blob> {
    try {
      const response = await this.api.post(`/dashboard/export/${type}`, {
        format,
        filters,
      }, {
        responseType: 'blob',
      });

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al exportar datos');
    }
  }

  // Estadísticas del dashboard
  async getDashboardStats(): Promise<any> {
    try {
      const response = await this.api.get<ApiResponse>('/dashboard/stats');

      if (response.data.success) {
        return response.data.data || {};
      } else {
        throw new Error(response.data.message || 'Error al obtener estadísticas');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al obtener estadísticas del dashboard');
    }
  }

  // Notificaciones
  async getNotifications(limit: number = 20): Promise<any[]> {
    try {
      const response = await this.api.get<ApiResponse<any[]>>(`/dashboard/notifications?limit=${limit}`);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        return [];
      }
    } catch (error: any) {
      console.error('Error al obtener notificaciones:', error);
      return [];
    }
  }

  // Marcar notificación como leída
  async markNotificationAsRead(notificationId: string): Promise<void> {
    try {
      await this.api.put(`/dashboard/notifications/${notificationId}/read`);
    } catch (error: any) {
      console.error('Error al marcar notificación como leída:', error);
    }
  }
}

// Crear instancia singleton
export const dashboardService = new DashboardService();

// Enviar mensaje (texto/imágenes/documentos) a conversación
export async function sendMessage(
  chatId: number,
  payload: { message: string; type?: 'text' | 'image' | 'document'; mediaUrl?: string; filename?: string; caption?: string }
) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('dashboard_token') : null;
  try {
    const res = await fetch(`/api/dashboard/conversations/${chatId}/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data?.message || 'Error enviando mensaje');
    }
    return data.data;
  } catch (err) {
    console.error('sendMessage error:', err);
    throw err;
  }
}

// Subir archivo y enviar como mensaje
export async function sendFile(
  chatId: number,
  file: File,
  options?: { caption?: string; typeHint?: 'image' | 'document'; onProgress?: (percent: number) => void }
): Promise<any> {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.caption) formData.append('caption', options.caption);
  if (options?.typeHint) formData.append('type', options.typeHint);

  const token = typeof window !== 'undefined' ? localStorage.getItem('dashboard_token') : null;

  try {
    const res = await axios.post(`/api/dashboard/conversations/${chatId}/upload`, formData, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      onUploadProgress: (evt: any) => {
        try {
          // Usar evt.total, o si no está disponible (Nginx reverse proxy), usar file.size como fallback
          const total = evt?.total || file.size;
          if (typeof total === 'number' && total > 0 && typeof evt?.loaded === 'number') {
            const percent = Math.max(1, Math.min(99, Math.round((evt.loaded / total) * 100)));
            options?.onProgress?.(percent);
          } else if (typeof evt?.progress === 'number') {
            options?.onProgress?.(Math.max(1, Math.min(99, Math.round(evt.progress * 100))));
          }
        } catch { /* noop */ }
      }
    });
    const data = res.data;
    if (!data?.success) {
      throw new Error(data?.message || 'Error enviando archivo');
    }
    return data.data;
  } catch (err: any) {
    console.error('sendFile error:', err);
    const msg = err?.response?.data?.message || err?.message || 'Error enviando archivo';
    throw new Error(msg);
  }
}

/**
 * Elimina completamente un chat y todos sus registros relacionados
 * Solo disponible para ADMIN
 */
export async function deleteConversation(chatId: string): Promise<ApiResponse> {
  try {
    const token = localStorage.getItem('dashboard_token');
    const response = await axios.delete(`${API_BASE_URL}/dashboard/conversations/${chatId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (err: any) {
    console.error('deleteConversation error:', err);
    throw err;
  }
}

export default dashboardService;

